/**
 * All the data fetching for the board, in one hook.
 *
 * Two independent loads:
 *   - the run list (sidebar), fetched once
 *   - the selected run's targets, refetched whenever the run or the sort changes
 *
 * Sorting is server-side. It could be done in the browser for a board that fits
 * in one response, but the API pages and the sort is what decides which rows are
 * IN that page — sorting client-side would reorder only the first 500 rows and
 * silently show the wrong top of the board.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ApiError,
  cancelRun,
  deleteRun,
  getLatest,
  getRun,
  listDeadEnds,
  listKgContradictions,
  listRuns,
  listTargets,
  type TokenGetter,
} from '../api/leaderboard'
import type {
  DeadEnd,
  DeadEndPage,
  KgContradiction,
  RunDetail,
  RunSummary,
  SortColumn,
  SortDirection,
  Target,
} from '../api/types'
import { DEFAULT_PAGE_SIZE } from '../api/types'

export interface LeaderboardState {
  runs: RunSummary[]
  /**
   * Whether `runs` is an answer. Same three-state rule as the dead ends below, and
   * it exists for the same reason: `runs` empty is *also* how this hook looks while
   * the landing fetch is in flight and after it failed, and the rail read that as
   * "no runs recorded yet — start one" in all three. Advice on a failed fetch is a
   * wrong statement, not a missing one, and the loading case flashed it on every
   * cold load.
   */
  runsLoaded: boolean
  selectedRunId: string | null
  run: RunSummary | RunDetail | null
  targets: Target[]
  total: number
  sort: SortColumn
  direction: SortDirection
  /** Rows per page, and the index of the first row currently shown. */
  limit: number
  offset: number
  /**
   * What review ruled out in the selected run, and how many there are in total.
   * `deadEnds` empty with `deadEndTotal === 0` means nothing was disqualified,
   * which is a result — the caller must not render it as "not recorded".
   * `deadEndsLoaded` distinguishes the two: false means the fetch has not
   * answered yet.
   *
   * `deadEndCounts` is the per-verdict breakdown for the whole run, from the API.
   * It is not derivable from `deadEnds`, which is one capped page — counting that
   * array would under-report the moment a run exceeds the page size.
   */
  deadEnds: DeadEnd[]
  deadEndTotal: number
  deadEndCounts: Record<string, number>
  /**
   * The same per-verdict breakdown with superseded rows removed, and how the API
   * arrived at it. `undefined` from a runtime that predates the field, which the
   * table renders as the recorded counts rather than as zeros.
   */
  deadEndFinal: DeadEndPage['final'] | undefined
  deadEndsLoaded: boolean
  /**
   * Which KG fields the selected run disputed, with the source it cited. Same
   * three-state rule as the dead ends and for the same reason: empty with
   * `kgContradictionTotal === 0` means *this run disputed nothing*, which is the
   * normal outcome; `kgContradictionsLoaded === false` means we do not know.
   * Rendering the second as the first would claim the graph went unchallenged.
   */
  kgContradictions: KgContradiction[]
  kgContradictionTotal: number
  kgContradictionsLoaded: boolean
  loading: boolean
  error: string | null
  /** True when the API answered 401 — the App swaps in the sign-in gate. */
  unauthorized: boolean
  /** Run id whose stop request is in flight, so the button can disable itself. */
  stopping: string | null
  /** Outcome of the last stop request — shown until the next one. */
  stopNotice: string | null
  /** Run id whose delete is in flight, so its row can disable itself. */
  deleting: string | null
  /**
   * Why the last delete was refused, or null. Only ever set on failure: a
   * successful delete removes the row, which is its own confirmation.
   */
  deleteError: string | null
  selectRun: (runId: string) => void
  toggleSort: (column: SortColumn) => void
  setOffset: (n: number) => void
  setPageSize: (n: number) => void
  refresh: () => void
  stopRun: (runId: string) => void
  removeRun: (runId: string) => void
}

export function useLeaderboard(
  getToken: TokenGetter,
  enabled: boolean,
): LeaderboardState {
  const [runs, setRuns] = useState<RunSummary[]>([])
  const [runsLoaded, setRunsLoaded] = useState(false)
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [run, setRun] = useState<RunSummary | RunDetail | null>(null)
  const [targets, setTargets] = useState<Target[]>([])
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState<SortColumn>('rank')
  const [direction, setDirection] = useState<SortDirection>('asc')
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)
  const [offset, setOffset] = useState(0)
  const [deadEnds, setDeadEnds] = useState<DeadEnd[]>([])
  const [deadEndTotal, setDeadEndTotal] = useState(0)
  const [deadEndCounts, setDeadEndCounts] = useState<Record<string, number>>({})
  const [deadEndFinal, setDeadEndFinal] = useState<DeadEndPage['final'] | undefined>()
  const [deadEndsLoaded, setDeadEndsLoaded] = useState(false)
  const [kgContradictions, setKgContradictions] = useState<KgContradiction[]>([])
  const [kgContradictionTotal, setKgContradictionTotal] = useState(0)
  const [kgContradictionsLoaded, setKgContradictionsLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)
  const [nonce, setNonce] = useState(0)
  const [stopping, setStopping] = useState<string | null>(null)
  const [stopNotice, setStopNotice] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  /**
   * What the rows currently in state represent. A ref, not state: it is written
   * by the effects that fetch, and as state it would re-trigger them.
   *
   * It replaces an earlier "skip when sort is rank-ascending and the run matches"
   * guard, which could not see the page. With paging that guard skipped the fetch
   * for page 2 of a rank-ascending board — the offset changed, nothing refetched,
   * and page 1's rows stayed on screen labelled as page 2.
   */
  const loadedKey = useRef<string | null>(null)
  const pageKey = `${selectedRunId}|${sort}|${direction}|${limit}|${offset}`

  /**
   * The page size, readable by the landing effect without being one of its
   * dependencies — that effect also *chooses* the selected run, so re-running it
   * on a page-size change would jump the selection back to whichever run last
   * ranked something.
   */
  const limitRef = useRef(limit)
  limitRef.current = limit

  const handleError = useCallback((e: unknown) => {
    if (e instanceof DOMException && e.name === 'AbortError') return
    if (e instanceof ApiError && e.status === 401) {
      setUnauthorized(true)
      return
    }
    setError(e instanceof Error ? e.message : String(e))
  }, [])

  // ── Landing load: run list + the newest run that produced targets ──────────
  // /latest rather than runs[0]: the newest run may be queued or failed with an
  // empty board, and landing on that reads as "the agent found nothing".
  useEffect(() => {
    if (!enabled) return
    const ac = new AbortController()
    setLoading(true)
    setError(null)

    void (async () => {
      try {
        const [runList, latest] = await Promise.all([
          listRuns(getToken, ac.signal),
          getLatest(getToken, ac.signal, limitRef.current),
        ])
        if (ac.signal.aborted) return
        setRuns(runList)
        // Only ever set, never cleared: it says "the array in `runs` came from a
        // fetch that answered", and a *later* refresh that fails leaves the last
        // good list on screen rather than replacing it with a loading line.
        setRunsLoaded(true)
        setUnauthorized(false)
        if (latest.run) {
          // Stamped before the state updates, and synchronously — the paged
          // effect runs as soon as `selectedRunId` lands and reads this to know
          // /latest already answered for that exact page.
          loadedKey.current = `${latest.run.run_id}|rank|asc|${limitRef.current}|0`
          setSelectedRunId(latest.run.run_id)
          setRun(latest.run)
          setTargets(latest.targets)
          setTotal(latest.total)
        }
        // Sort, direction and offset are deliberately left alone. /latest always
        // returns page 1 rank-ascending, so if the reader had chosen otherwise
        // the key above no longer matches and the paged effect refetches under
        // their choice — a refresh must not silently discard it.
      } catch (e) {
        handleError(e)
      } finally {
        if (!ac.signal.aborted) setLoading(false)
      }
    })()

    return () => ac.abort()
  }, [enabled, getToken, handleError, nonce])

  // ── Selected run's board ──────────────────────────────────────────────────
  // Skipped for the page /latest already returned, so the landing pass does not
  // double-fetch its own rows.
  useEffect(() => {
    if (!enabled || !selectedRunId) return
    if (loadedKey.current === pageKey) return
    const ac = new AbortController()
    setLoading(true)

    void (async () => {
      try {
        const [detail, page] = await Promise.all([
          getRun(selectedRunId, getToken, ac.signal),
          listTargets(
            selectedRunId,
            { sort, direction, limit, offset },
            getToken,
            ac.signal,
          ),
        ])
        if (ac.signal.aborted) return
        loadedKey.current = pageKey
        setRun(detail)
        setTargets(page.targets)
        setTotal(page.total)
        setError(null)
      } catch (e) {
        handleError(e)
      } finally {
        if (!ac.signal.aborted) setLoading(false)
      }
    })()

    return () => ac.abort()
  }, [
    enabled,
    selectedRunId,
    sort,
    direction,
    limit,
    offset,
    pageKey,
    getToken,
    handleError,
  ])

  // ── What the selected run ruled out ───────────────────────────────────────
  // Its own request, not folded into the effect above: it does not change with
  // the sort or the page, and it must keep answering when the ranked board is
  // empty — a run that disqualified everything is the case where this list is
  // the whole result.
  useEffect(() => {
    if (!enabled || !selectedRunId) return
    const ac = new AbortController()
    setDeadEndsLoaded(false)
    setDeadEnds([])
    setDeadEndTotal(0)
    setDeadEndCounts({})

    void (async () => {
      try {
        const page = await listDeadEnds(selectedRunId, {}, getToken, ac.signal)
        if (ac.signal.aborted) return
        setDeadEnds(page.dead_ends)
        setDeadEndTotal(page.total)
        // `?? {}` because a runtime older than the counts field answers without
        // it. Empty is the honest reading: the table falls back to counting the
        // page, and says so, rather than showing three zeroed groups.
        setDeadEndCounts(page.counts ?? {})
        // `undefined`, not a fabricated block: an older runtime never computed
        // supersession, and the table must fall back to the recorded counts rather
        // than report every row as final.
        setDeadEndFinal(page.final)
        setDeadEndsLoaded(true)
      } catch (e) {
        // Not `handleError`: a failure here must not replace the board's error
        // banner or blank the ranking. `deadEndsLoaded` stays false, which the
        // table renders as "not recorded" rather than as "nothing was ruled out".
        if (e instanceof ApiError && e.status === 401) setUnauthorized(true)
      }
    })()

    return () => ac.abort()
  }, [enabled, selectedRunId, nonce, getToken])

  // ── Where the selected run disputed the knowledge graph ────────────────────
  // Its own request for the same reasons as the dead ends: independent of the
  // sort and the page, and it must keep answering when the ranked board is empty.
  // A run can dispute a field on a hypothesis that was later disqualified, so
  // this list is not a subset of anything above it.
  useEffect(() => {
    if (!enabled || !selectedRunId) return
    const ac = new AbortController()
    setKgContradictionsLoaded(false)
    setKgContradictions([])
    setKgContradictionTotal(0)

    void (async () => {
      try {
        const page = await listKgContradictions(
          selectedRunId,
          {},
          getToken,
          ac.signal,
        )
        if (ac.signal.aborted) return
        setKgContradictions(page.kg_contradictions)
        setKgContradictionTotal(page.total)
        setKgContradictionsLoaded(true)
      } catch (e) {
        // Not `handleError`, same rule as above: this must not replace the
        // board's error banner. Leaving `kgContradictionsLoaded` false is what
        // makes the panel say "could not read" instead of "none found".
        if (e instanceof ApiError && e.status === 401) setUnauthorized(true)
      }
    })()

    return () => ac.abort()
  }, [enabled, selectedRunId, nonce, getToken])

  const selectRun = useCallback((runId: string) => {
    setSelectedRunId(runId)
    // Reset to rank-ascending: carrying a protein-descending sort across runs
    // makes a newly opened board look unranked.
    setSort('rank')
    setDirection('asc')
    // And back to page 1 — runs have different lengths, so page 3 of one is
    // often past the end of the next, which reads as an empty ranking.
    setOffset(0)
    setTargets([])
    setRun(null)
  }, [])

  const setPageSize = useCallback((n: number) => {
    setOffset(0)
    setLimit(n)
  }, [])

  const toggleSort = useCallback(
    (column: SortColumn) => {
      // Page 1, always: an offset kept across a re-ordering pages into a
      // different set of rows than the one the reader is looking at.
      setOffset(0)
      // Reads the current sort/direction from the closure rather than nesting
      // setDirection inside a setSort updater: updaters must be pure, and
      // StrictMode invokes them twice, which would toggle the direction back to
      // where it started on every click.
      if (sort === column) {
        setDirection(direction === 'asc' ? 'desc' : 'asc')
        return
      }
      setSort(column)
      // Elo, BTL and recency are "higher is better", so a first click on them
      // should show the leaders. Rank and the text columns read best ascending.
      setDirection(
        column === 'elo' || column === 'btl_score' || column === 'created_at'
          ? 'desc'
          : 'asc',
      )
    },
    [sort, direction],
  )

  const refresh = useCallback(() => {
    setUnauthorized(false)
    setNonce((n) => n + 1)
  }, [])

  // ── Stop a running sweep ──────────────────────────────────────────────────
  // Deliberately does NOT optimistically set the row to `cancelled`. The sweep
  // lives in another container and takes up to ~10 s to notice; showing it as
  // stopped before it has stopped would let someone start a second sweep on the
  // same thread while the first is still writing to that checkpoint.
  const stopRun = useCallback(
    (runId: string) => {
      setStopping(runId)
      setStopNotice(null)
      void (async () => {
        try {
          const result = await cancelRun(runId, getToken)
          setStopNotice(result.message)
          // Refetch the run list rather than patch it locally: `reaped` already
          // changed the row and `requested` has not, and the difference is
          // exactly what the operator needs to see. Not `refresh()` — that also
          // reloads /latest and would move the selection to whichever run last
          // produced targets, which is never the one just stopped.
          const runList = await listRuns(getToken)
          setRuns(runList)
          const updated = runList.find((r) => r.run_id === runId)
          if (updated) {
            // Merged, not replaced: the selected run may be a RunDetail and
            // target_count is not in the list payload.
            setRun((prev) =>
              prev && prev.run_id === runId ? { ...prev, ...updated } : prev,
            )
          }
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            setUnauthorized(true)
          } else {
            setStopNotice(
              `Could not stop the run: ${e instanceof Error ? e.message : String(e)}`,
            )
          }
        } finally {
          setStopping(null)
        }
      })()
    },
    [getToken],
  )

  // ── Delete a finished run that produced nothing ────────────────────────────
  // Housekeeping for the sidebar: failed and cancelled sweeps accumulate and
  // bury the runs that found something. The API refuses anything holding ranked
  // targets, so this cannot remove a result — a 409 explaining that is the
  // expected answer for a misdirected click, not an error to swallow.
  const removeRun = useCallback(
    (runId: string) => {
      setDeleting(runId)
      setDeleteError(null)
      void (async () => {
        try {
          await deleteRun(runId, getToken)
          // Dropped locally rather than by refetching: the row is gone
          // server-side and nothing else about the list changed. Deselecting
          // matters — leaving the selection on a deleted run would keep its
          // header on screen with no row behind it.
          setRuns((prev) => prev.filter((r) => r.run_id !== runId))
          setSelectedRunId((prev) => (prev === runId ? null : prev))
          setRun((prev) => (prev?.run_id === runId ? null : prev))
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            setUnauthorized(true)
          } else {
            setDeleteError(e instanceof Error ? e.message : String(e))
          }
        } finally {
          setDeleting(null)
        }
      })()
    },
    [getToken],
  )

  return {
    runs,
    runsLoaded,
    selectedRunId,
    run,
    targets,
    total,
    sort,
    direction,
    limit,
    offset,
    deadEnds,
    deadEndTotal,
    deadEndCounts,
    deadEndFinal,
    deadEndsLoaded,
    kgContradictions,
    kgContradictionTotal,
    kgContradictionsLoaded,
    loading,
    error,
    unauthorized,
    stopping,
    stopNotice,
    deleting,
    deleteError,
    selectRun,
    toggleSort,
    setOffset,
    setPageSize,
    refresh,
    stopRun,
    removeRun,
  }
}
